import React, { useState, useEffect } from "react";
import {
    X,
    User,
    Calendar,
    Mail,
    Phone,
    MapPin,
    CreditCard,
    ChevronRight,
    AlertCircle
} from "lucide-react";
import { createStudent, updateStudent } from "../services/studentService";
import "../pages/MasterStudents/MasterStudent.css"; // Reuse existing styles

const getTodayDate = () => new Date().toISOString().split('T')[0];

const initialFormState = {
    name: "",
    dob: "",
    branchCourseId: "",
    joiningDate: getTodayDate(),
    fees: "",
    feeRecurrence: "Monthly",
    currentProgressLevel: "Level-1",
    grade: "",
    batchId: "",
    roleId: 3,
    Parentname: "",
    password: "",
    email: "",
    phoneNumber: ""
};

const StudentFormModal = ({
    isOpen,
    onClose,
    onSuccess,
    isEditMode = false,
    isViewMode = false,
    prefillData = null,
    selectedItem = null,
    branchCourseList = [],
    batches = []
}) => {
    const [formData, setFormData] = useState(initialFormState);
    const [pin, setPin] = useState(["", "", "", ""]);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showMappingDropdown, setShowMappingDropdown] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [popup, setPopup] = useState({ show: false, title: "", message: "", type: "success" });

    useEffect(() => {
        if (isOpen) {
            if (prefillData) {
                setFormData({
                    ...initialFormState,
                    ...prefillData,
                    joiningDate: prefillData.joiningDate || getTodayDate(),
                });
                setPin(["", "", "", ""]);
            } else if (selectedItem) {
                setFormData({
                    name: selectedItem.Name || "",
                    dob: selectedItem.DateOfBirth || selectedItem.DOB ? (selectedItem.DateOfBirth || selectedItem.DOB).split('T')[0] : "",
                    branchCourseId: selectedItem.BranchCourseId || "",
                    joiningDate: selectedItem.JoiningDate ? selectedItem.JoiningDate.split('T')[0] : "",
                    fees: selectedItem.Fees || "",
                    feeRecurrence: selectedItem.FeeRecurrence || "Monthly",
                    currentProgressLevel: selectedItem.CurrentProgressLevel || "",
                    grade: selectedItem.Grade || "",
                    batchId: selectedItem.BatchId || "",
                    roleId: selectedItem.RoleId || 3,
                    Parentname: selectedItem.Parentname || selectedItem.ParentName || selectedItem.UserName || "",
                    password: "",
                    email: selectedItem.Email || "",
                    phoneNumber: selectedItem.PhoneNumber || ""
                });
                setPin(isViewMode ? ["*", "*", "*", "*"] : ["", "", "", ""]);
            } else {
                setFormData(initialFormState);
                setPin(["", "", "", ""]);
            }
            setErrors({});
            setShowMappingDropdown(false);
            setShowDiscardModal(false);
        }
    }, [isOpen, prefillData, selectedItem, isViewMode]);

    if (!isOpen) return null;

    const showPopup = (title, message, type = "success") => {
        setPopup({ show: true, title, message, type });
    };

    const validateForm = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^\d{10}$/;
        const nameRegex = /^[a-zA-Z\s.]{2,50}$/;

        if (!formData.name?.trim()) {
            newErrors.name = "Student Name is required";
        } else if (!nameRegex.test(formData.name)) {
            newErrors.name = "Letters and spaces only (2-50 chars)";
        }

        if (!formData.branchCourseId) newErrors.branchCourseId = "Branch & Course are required";
        if (!formData.batchId) newErrors.batchId = "Batch selection is required";

        if (formData.Parentname?.trim() && !nameRegex.test(formData.Parentname)) {
            newErrors.Parentname = "Letters and spaces only";
        }

        if (!formData.phoneNumber) {
            newErrors.phoneNumber = "Phone Number is required";
        } else if (!phoneRegex.test(formData.phoneNumber)) {
            newErrors.phoneNumber = "Must be exactly 10 digits";
        }

        if (formData.email?.trim() && !emailRegex.test(formData.email)) {
            newErrors.email = "Invalid email format";
        }

        if (!isEditMode) {
            if (formData.password && formData.password.toString().length < 4) {
                newErrors.password = "Min 4 digits required";
            }
        }

        if (!formData.fees || parseFloat(formData.fees) <= 0) {
            newErrors.fees = "Valid Fees amount is required";
        }

        if (!formData.joiningDate) {
            newErrors.joiningDate = "Joining Date is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePinChange = (value, index) => {
        if (!/^\d?$/.test(value)) return;
        const newPin = [...pin];
        newPin[index] = value;
        setPin(newPin);
        const combinedPin = newPin.join("");
        setFormData(prev => ({ ...prev, password: combinedPin }));
        if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
        if (value && index < 3) {
            document.getElementById(`reg-pin-${index + 1}`)?.focus();
        }
    };

    const handlePinKeyDown = (e, index) => {
        if (e.key === "Backspace" && !pin[index] && index > 0) {
            document.getElementById(`reg-pin-${index - 1}`)?.focus();
        }
    };

    const handleSave = async () => {
        if (!validateForm()) {
            showPopup("Validation Error", "Please fill all mandatory fields correctly", "error");
            return;
        }

        try {
            setIsLoading(true);
            if (isEditMode && selectedItem) {
                const updatePayload = {
                    StudentId: selectedItem.StudentId,
                    Name: formData.name,
                    DateOfBirth: formData.dob || null,
                    Fees: parseFloat(formData.fees) || 0,
                    FeeRecurrence: formData.feeRecurrence,
                    CurrentProgressLevel: formData.currentProgressLevel,
                    Grade: formData.grade,
                    BranchCourseId: parseInt(formData.branchCourseId),
                    BatchId: parseInt(formData.batchId),
                    Email: formData.email,
                    PhoneNumber: formData.phoneNumber,
                    JoiningDate: formData.joiningDate,
                    ParentName: formData.Parentname?.trim() || "",
                };
                await updateStudent(updatePayload);
                if (onSuccess) onSuccess("Student updated successfully!");
            } else {
                const createPayload = {
                    Name: formData.name,
                    DateOfBirth: formData.dob,
                    BranchCourseId: parseInt(formData.branchCourseId),
                    JoiningDate: formData.joiningDate,
                    Fees: parseFloat(formData.fees) || 0,
                    FeeRecurrence: formData.feeRecurrence,
                    CurrentProgressLevel: formData.currentProgressLevel,
                    Grade: formData.grade,
                    BatchId: parseInt(formData.batchId),
                    RoleId: 3,
                    ParentName: formData.Parentname?.trim() || "",
                    Password: formData.password ? parseInt(formData.password) : 1234,
                    Email: formData.email,
                    PhoneNumber: formData.phoneNumber
                };
                await createStudent(createPayload);
                if (onSuccess) onSuccess("Student created successfully!");
            }
        } catch (err) {
            console.error("Save Error:", err);
            const errorMsg = typeof err === "string" ? err : (err?.response?.data?.message || err?.message || "An error occurred during save");
            showPopup("Submission Failed", errorMsg, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const checkIfDirty = () => {
        if (isViewMode) return false;
        let initial = isEditMode && selectedItem ? {
            name: selectedItem.Name || "",
            dob: selectedItem.DOB || selectedItem.DateOfBirth ? (selectedItem.DOB || selectedItem.DateOfBirth).split('T')[0] : "",
            branchCourseId: selectedItem.BranchCourseId || "",
            joiningDate: selectedItem.JoiningDate ? selectedItem.JoiningDate.split('T')[0] : "",
            fees: selectedItem.Fees || "",
            feeRecurrence: selectedItem.FeeRecurrence || "Monthly",
            currentProgressLevel: selectedItem.CurrentProgressLevel || "",
            grade: selectedItem.Grade || "",
            batchId: selectedItem.BatchId || "",
            roleId: selectedItem.RoleId || 3,
            Parentname: selectedItem.Parentname || selectedItem.ParentName || selectedItem.UserName || "",
            password: "",
            email: selectedItem.Email || "",
            phoneNumber: selectedItem.PhoneNumber || ""
        } : (prefillData ? { ...initialFormState, ...prefillData } : initialFormState);

        return JSON.stringify(formData) !== JSON.stringify(initial);
    };

    const handleCloseModal = () => {
        if (checkIfDirty()) {
            setShowDiscardModal(true);
        } else {
            if (onClose) onClose();
        }
    };

    const confirmDiscard = () => {
        setShowDiscardModal(false);
        if (onClose) onClose();
    };

    return (
        <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-card wide student-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{isViewMode ? "View Student Details" : isEditMode ? "Edit Student" : "Register New Student"}</h3>
                    <button className="close-btn" onClick={handleCloseModal}><X size={20} /></button>
                </div>
                <div className="modal-body scrollable">
                    <div className="student-form-layout">
                        <div className="form-section">
                            <h4><User size={16} /> Basic Information</h4>
                            <div className="form-grid-inner">
                                <FormInput label="Full Name" name="name" isAlpha value={formData.name} onChange={setFormData} icon={<User size={16} />} error={errors.name} setErrors={setErrors} readOnly={isViewMode} />
                                <FormInput label="Date of Birth" name="dob" type="date" value={formData.dob} onChange={setFormData} icon={<Calendar size={16} />} error={errors.dob} setErrors={setErrors} readOnly={isViewMode} />
                                <FormInput label="Email Address (Optional)" name="email" type="email" value={formData.email} onChange={setFormData} icon={<Mail size={16} />} error={errors.email} setErrors={setErrors} readOnly={isViewMode} />
                                <FormInput label="Phone Number" name="phoneNumber" isNumeric maxLength={10} value={formData.phoneNumber} onChange={setFormData} icon={<Phone size={16} />} error={errors.phoneNumber} setErrors={setErrors} readOnly={isViewMode} />
                            </div>
                        </div>

                        <div className="form-section">
                            <h4><MapPin size={16} /> Academic & Batch Details</h4>
                            <div className="form-grid-inner">
                                <div className={`form-input-group ${errors.branchCourseId ? 'has-error' : ''}`}>
                                    <label>Branch & Course Mapping</label>
                                    <div
                                        className={`custom-select-wrapper ${isViewMode ? 'disabled' : ''} ${showMappingDropdown ? 'open' : ''} ${errors.branchCourseId ? 'error-border' : ''}`}
                                        onClick={() => !isViewMode && setShowMappingDropdown(!showMappingDropdown)}
                                    >
                                        <div className="selected-value">
                                            {formData.branchCourseId ? (
                                                branchCourseList.find(group => group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.CourseList.find(c => String(c.BranchCourseId) === String(formData.branchCourseId)) ? (
                                                    `${branchCourseList.find(group => group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.CourseList.find(c => String(c.BranchCourseId) === String(formData.branchCourseId))?.CourseName} - ${branchCourseList.find(group => group.CourseList.some(c => String(c.BranchCourseId) === String(formData.branchCourseId)))?.BranchName}`
                                                ) : "Select Course - Branch"
                                            ) : "Select Course - Branch"}
                                        </div>
                                        <ChevronRight size={18} className="select-arrow" />

                                        {showMappingDropdown && (
                                            <div className="custom-dropdown-options">
                                                <div
                                                    className="custom-option"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setFormData({ ...formData, branchCourseId: "", batchId: "" });
                                                        setErrors(prev => ({ ...prev, branchCourseId: "" }));
                                                        setShowMappingDropdown(false);
                                                    }}
                                                >
                                                    Select Course - Branch
                                                </div>
                                                {branchCourseList.map((group, gIdx) => (
                                                    <div key={`branch-${group.BranchId}-${gIdx}`} className="option-group">
                                                        <div className="group-label">{group.BranchName}</div>
                                                        {group.CourseList.map((course, cIdx) => (
                                                            <div
                                                                key={`course-${course.BranchCourseId}-${cIdx}`}
                                                                className={`custom-option ${String(formData.branchCourseId) === String(course.BranchCourseId) ? 'active' : ''}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (String(formData.branchCourseId) !== String(course.BranchCourseId)) {
                                                                        setFormData({ ...formData, branchCourseId: course.BranchCourseId, batchId: "" });
                                                                    }
                                                                    setErrors(prev => ({ ...prev, branchCourseId: "" }));
                                                                    setShowMappingDropdown(false);
                                                                }}
                                                            >
                                                                {course.CourseName}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {errors.branchCourseId && <span className="error-message-inline">{errors.branchCourseId}</span>}
                                </div>
                                <FormInput label="Joining Date" name="joiningDate" type="date" value={formData.joiningDate} onChange={setFormData} error={errors.joiningDate} setErrors={setErrors} readOnly={isViewMode} />
                                <div className={`form-input-group ${errors.batchId ? 'has-error' : ''}`}>
                                    <label>Batch Selection</label>
                                    <select
                                        value={formData.batchId}
                                        onChange={(e) => {
                                            setFormData({ ...formData, batchId: e.target.value });
                                            setErrors(prev => ({ ...prev, batchId: "" }));
                                        }}
                                        disabled={isViewMode || !formData.branchCourseId}
                                        className={`form-select-modern ${errors.batchId ? 'error-border' : ''} ${isViewMode ? 'readonly-input' : ''}`}
                                    >
                                        <option value="">{formData.branchCourseId ? "Choose Batch" : "Select Branch & Course First"}</option>
                                        {batches
                                            .filter(batch => String(batch.BranchCourseId) === String(formData.branchCourseId))
                                            .map((batch, bIdx) => (
                                                <option key={`batch-${batch.BatchId || batch.id}-${bIdx}`} value={batch.BatchId || batch.id}>
                                                    {batch.BatchName || batch.name} {batch.BatchTime ? `(${batch.BatchTime})` : ''}
                                                </option>
                                            ))
                                        }
                                    </select>
                                    {errors.batchId && <span className="error-message-inline">{errors.batchId}</span>}
                                </div>
                                <FormInput label="Grade / Standard (Optional)" name="grade" value={formData.grade} onChange={setFormData} error={errors.grade} setErrors={setErrors} readOnly={isViewMode} />
                            </div>

                            <div className="level-picker-container">
                                <label>Current Progress Level</label>
                                <div className="progress-level-selector">
                                    {["Level-1", "Level-2", "Level-3", "Level-4", "Level-5"].map(level => (
                                        <button
                                            key={level}
                                            className={`level-pill ${formData.currentProgressLevel === level ? 'active' : ''} ${isViewMode ? 'disabled-pill' : ''}`}
                                            onClick={() => !isViewMode && setFormData({ ...formData, currentProgressLevel: level })}
                                        >
                                            {level}
                                        </button>
                                    ))}
                                </div>
                                <div className="progress-bar-visual">
                                    <div
                                        className="progress-fill"
                                        style={{ width: `${(parseInt(formData.currentProgressLevel.split('-')[1]) / 5) * 100}%` }}
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h4><CreditCard size={16} /> Fees & Security</h4>
                            <div className="form-grid-inner">
                                <FormInput label="Total Fees" name="fees" type="number" value={formData.fees} onChange={setFormData} error={errors.fees} setErrors={setErrors} readOnly={isViewMode} />
                                <div className={`form-input-group`}>
                                    <label>Fee Recurrence</label>
                                    <select
                                        value={formData.feeRecurrence}
                                        onChange={(e) => setFormData({ ...formData, feeRecurrence: e.target.value })}
                                        disabled={isViewMode}
                                        className={`form-select-modern ${isViewMode ? 'readonly-input' : ''}`}
                                    >
                                        <option value="Monthly">Monthly</option>
                                        <option value="Quarterly">Quarterly</option>
                                        <option value="Annually">Annually</option>
                                    </select>
                                </div>
                                <FormInput label="Parent Name (Optional)" name="Parentname" value={formData.Parentname} onChange={setFormData} error={errors.Parentname} setErrors={setErrors} readOnly={isViewMode} />
                                {!isEditMode && (
                                    <div className={`form-input-group ${errors.password ? "has-error" : ""}`}>
                                        <label>Set 4-Digit PIN (Optional)</label>
                                        <div className="pin-digits-container">
                                            {pin.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    id={`reg-pin-${index}`}
                                                    type="password"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    maxLength={1}
                                                    value={digit}
                                                    onChange={(e) => !isViewMode && handlePinChange(e.target.value, index)}
                                                    onKeyDown={(e) => !isViewMode && handlePinKeyDown(e, index)}
                                                    className={`pin-digit-box ${errors.password ? "error-border" : ""} ${isViewMode ? "readonly-input" : ""}`}
                                                    autoComplete="new-password"
                                                    readOnly={isViewMode}
                                                />
                                            ))}
                                        </div>
                                        {errors.password && <span className="error-message-inline">{errors.password}</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="cancel-btn" onClick={handleCloseModal} disabled={isLoading}>{isViewMode ? "Close" : "Cancel"}</button>
                    {!isViewMode && (
                        <button className="save-student-btn-premium" onClick={handleSave} disabled={isLoading}>
                            {isLoading ? "Saving..." : (isEditMode ? "Update Student" : "Onboard Student")}
                        </button>
                    )}
                </div>
            </div>

            {/* DISCARD CHANGES CONFIRMATION */}
            {showDiscardModal && (
                <div className="modal-overlay" onClick={() => setShowDiscardModal(false)} style={{ zIndex: 1100 }}>
                    <div className="modal-card mini" onClick={(e) => e.stopPropagation()}>
                        <div className="delete-confirm-body">
                            <AlertCircle size={48} color="#f59e0b" />
                            <h3>Discard Changes?</h3>
                            <p>You have unsaved changes. Are you sure you want to close? All details entered will be removed.</p>
                            <div className="confirm-actions">
                                <button className="cancel-btn" onClick={() => setShowDiscardModal(false)}>Keep Editing</button>
                                <button className="delete-confirm-btn" style={{ background: '#f59e0b' }} onClick={confirmDiscard}>Discard</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* POPUP */}
            {popup.show && (
                <div className="popup-overlay" onClick={() => setPopup({ ...popup, show: false })} style={{ zIndex: 1100 }}>
                    <div className="popup-card" onClick={(e) => e.stopPropagation()}>
                        <div className={`popup-icon ${popup.type}`}>
                            {popup.type === "success" ? "✓" : "!"}
                        </div>
                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>
                        <button className="popup-btn" onClick={() => setPopup({ ...popup, show: false })}>Got it</button>
                    </div>
                </div>
            )}
        </div>
    );
};

const FormInput = ({ label, name, value, isAlpha, onChange, type = "text", icon, error, isNumeric, maxLength, setErrors, readOnly }) => (
    <div className={`form-input-group ${error ? "has-error" : ""}`}>
        <label>{label}</label>
        <div className={`input-with-icon ${error ? "error-border" : ""} ${readOnly ? "readonly-input-wrap" : ""}`}>
            {icon && <span className="input-icon">{icon}</span>}
            <input
                type={type}
                value={value}
                onChange={(e) => {
                    if (readOnly) return;
                    let val = e.target.value;
                    if (isNumeric) {
                        val = val.replace(/\D/g, "");
                    }

                    if (isAlpha) {
                        val = val.replace(/[0-9]/g, "");
                    }
                    if (maxLength && val.length > maxLength) {
                        val = val.slice(0, maxLength);
                    }
                    onChange(prev => ({ ...prev, [name]: val }));
                    if (setErrors) {
                        setErrors(prev => ({ ...prev, [name]: "" }));
                    }
                }}
                placeholder={readOnly ? "" : `Enter ${label.toLowerCase()}`}
                readOnly={readOnly}
                className={readOnly ? "readonly-input" : ""}
            />
        </div>
        {error && <span className="error-message-inline">{error}</span>}
    </div>
);

export default StudentFormModal;
